import { TIMELINE, BRAND } from "../lib/constants";
import { Reveal, Stagger, itemFade } from "../components/Motion";
import { motion } from "framer-motion";
import { Compass, Sparkles, Shield, Award, Repeat } from "lucide-react";
import CTASection from "../components/CTASection";

const VALUES = [
  { icon: Compass, name: "Vision", desc: "We see the field three moves ahead." },
  { icon: Sparkles, name: "Innovation", desc: "Modern stacks, modern thinking." },
  { icon: Shield, name: "Integrity", desc: "What we promise is what we ship." },
  { icon: Award, name: "Excellence", desc: "Premium is a baseline, not an upgrade." },
  { icon: Repeat, name: "Consistency", desc: "We compound — release after release." },
];

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_elevara-digital/artifacts/qbyql5vu_8e9e56c651e6e98532036f47c303256af014e4fad5bbab4d503bd2fd3dba9988.png";

export default function About() {
  return (
    <main data-testid="about-page" className="pt-32">
      {/* HERO */}
      <section className="px-6 md:px-8 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">About {BRAND.name}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mt-6 max-w-5xl">
              A premium partner for the{" "}
              <em className="text-[#D4AF37] not-italic">long arc</em> of your business.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="px-6 md:px-8 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
          <Reveal>
            <div data-testid="mission-block" className="bg-[#0B0B0B] p-12 md:p-16 h-full">
              <span className="overline">Our Mission</span>
              <p className="font-display text-3xl md:text-4xl mt-6 leading-[1.15] tracking-tight">
                Helping entrepreneurs and organizations leverage technology,
                automation, and intellectual property to create{" "}
                <em className="text-[#D4AF37] not-italic">scalable and sustainable</em>{" "}
                growth.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="vision-block" className="bg-[#0B0B0B] p-12 md:p-16 h-full">
              <span className="overline">Our Vision</span>
              <p className="font-display text-3xl md:text-4xl mt-6 leading-[1.15] tracking-tight">
                To become a leading innovation partner connecting business
                strategy, technology, AI, and market expansion.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="px-6 md:px-8 pb-24 md:pb-32 bg-[#0E0E0E] border-y border-white/5 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Core Values</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 max-w-2xl leading-[1.05]">
              Five non-negotiables.
            </h2>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5 mt-16">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.name}
                variants={itemFade}
                data-testid={`value-${v.name.toLowerCase()}`}
                className="bg-[#0B0B0B] p-10 group hover:bg-[#141414] transition-colors"
              >
                <div className="w-11 h-11 border border-[#D4AF37]/30 flex items-center justify-center mb-8 group-hover:bg-[#D4AF37] transition-colors">
                  <v.icon size={18} strokeWidth={1.5} className="text-[#D4AF37] group-hover:text-[#0B0B0B]" />
                </div>
                <div className="font-mono text-xs text-white/30 mb-2">
                  0{i + 1}
                </div>
                <h3 className="font-display text-2xl md:text-[26px]">
                  {v.name}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mt-3">
                  {v.desc}
                </p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="px-6 md:px-8 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img
                src={FOUNDER_IMG}
                alt="Paxton Geng"
                data-testid="founder-portrait"
                className="w-full h-[560px] object-cover hover:scale-[1.02] transition-transform duration-700"
              />
              <div className="absolute inset-0 ring-1 ring-[#D4AF37]/20 pointer-events-none" />
              <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-[#0B0B0B] px-5 py-3 font-display text-sm">
                Founder & CEO
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <span className="overline">Leadership</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-5xl md:text-6xl mt-6 leading-[1] tracking-tight">
                Paxton Geng
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-[#D4AF37] mt-2 text-sm uppercase tracking-[0.22em]">
                Founder & CEO, Innapax
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-white/70 leading-relaxed text-base md:text-lg">
                Paxton founded Innapax to bridge a gap he saw repeatedly across
                growing companies: brilliant strategy without execution, or
                excellent engineering without business clarity. Elevara is the
                operating system for both.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-4 text-white/55 leading-relaxed">
                With a background spanning enterprise consulting, product
                engineering, and growth marketing, Paxton leads every client
                engagement with a focus on long-term value compounding.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="grid grid-cols-3 gap-8 mt-12 pt-10 border-t border-white/10">
                {[
                  { k: "12+", v: "Years Building" },
                  { k: "100+", v: "Engagements" },
                  { k: "6", v: "Industries" },
                ].map((s) => (
                  <div key={s.k}>
                    <div className="font-display text-3xl md:text-4xl text-[#D4AF37]">
                      {s.k}
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/50 mt-2">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="px-6 md:px-8 py-24 md:py-32 bg-[#0E0E0E] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Trajectory</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 max-w-2xl leading-[1.05]">
              A focused path forward.
            </h2>
          </Reveal>

          <div className="mt-16 relative">
            <div className="absolute left-0 right-0 top-7 h-px bg-white/10 hidden md:block" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.08}>
                  <div
                    data-testid={`timeline-${t.year}`}
                    className="relative md:pr-6"
                  >
                    <div className="hidden md:block absolute left-0 top-5 w-3 h-3 rounded-full bg-[#D4AF37] ring-4 ring-[#0E0E0E]" />
                    <div className="font-mono text-xs text-[#D4AF37] mb-4 md:mt-12">
                      {t.year}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl leading-tight">
                      {t.title}
                    </h3>
                    <p className="text-white/55 text-sm leading-relaxed mt-3">
                      {t.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection testId="about-cta" />
    </main>
  );
}
